import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getRosace, ensureRosaceGlass, preloadRosace } from '../rosace-assets.js';

// Shared-rig contract: forbidden. The rose is lit from BEHIND by its own sun;
// a wash from the front would put light on the leads and the stone, which is
// exactly what a rose window in a dark cathedral never has.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'rosace',
  family: 'photographic',
  danceability: 0.40,
  energyLevel: 0.38,
  bpmMin: 50,
  bpmMax: 140,
  movement: 0.35,
  tonality: 0.90,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.60] },
  enabled: true
};

// Rosace: a cathedral rose window, seen from inside, lit by a sun that
// crosses the sky over the length of the track.
//
// THE GLASS PIECES ARE THE PIXELS. tools/build-rosace.mjs cuts the photograph
// into its pieces of glass along the lead network at build time and ships an
// id map (one id per pixel, lossless) next to the photo. Here every piece
// carries ONE intensity, held in a 64 x 32 float texture indexed by id, and
// the shader multiplies the photo by it: a wave lights whole pieces, never a
// gradient across one, which is what makes the light read as light THROUGH
// the glass rather than a picture projected onto it. The photograph's own
// colours are the transmission; nothing here invents a colour.
//
// THE SUN IS THE CLOCK OF THE PIECE. Its position follows the track's
// progress, from low on the left to the zenith at mid-track to low on the
// right, and everything that is not a beat follows it: the gradient across
// the rose (the pieces facing the sun are the brightest), the colour of the
// light (orange low, white high), the glint on the sun side of the leads, the
// ellipse the rose throws on the floor and the shafts in the air. The beat
// events ride on top of that as ADDITIONS to the sun's level.

// --- geometry, in the shared camera's frame ----------------------------------
const CAMERA_D = 10.0;           // camera to wall
const PITCH_DEG = 5;             // the camera looks slightly UP: seen from below
const ROSE_Y_NDC = 0.25;         // rose centre, above the frame's centre
const ROSE_SHARE = 0.70;         // rose diameter as a share of the frame height
const FLOOR_NDC = -0.40;         // where the wall meets the floor: floor = bottom 30%
const COLUMN_X_NDC = 0.88;
const COLUMN_W_NDC = 0.09;
const FLOOR_DEPTH = 16.0;
const WALL_W = 60.0;

// --- light -------------------------------------------------------------------
const GRADIENT_MIN = 0.55;       // the piece facing away from the sun
const GRADIENT_MAX = 1.00;       // the piece facing it
const CAP = 1.6;
const ATTACK_S = 0.10;
const DECAY_S = 0.60;
const RADIAL_AMP = 0.30;
const ANGULAR_AMP = 0.30;
const PETAL_LEVEL = 1.5;
const DROP_LEVEL = 1.6;
const TRAIL = 0.33;              // the trail behind a front, per ring/sector
const RINGS = 6;                 // radial rings, one lit per half-beat
const SECTORS = 12;              // 30 degree sectors - one per petal
const PETAL_R = [0.25, 0.65];
const BAND_SWING = 0.20;         // +- 20% by the bands
const SUN_ORANGE = [1.0, 0.60, 0.26];
const PALETTE_TINT = 0.25;
const GLASS_GAIN = 2.2;          // photo (decoded) x light -> linear, calibrated by capture
const PROJ_OPACITY = 0.6;
const PROJ_BLUR_LOD = 2.6;       // ~6 px of blur at the floor's mip
const CLOUD_MIN = 0.5;
const CLOUD_IN_S = 3, CLOUD_OUT_S = 4;
const CLOUD_HOLD = [5, 15];
const CLOUD_EVERY = [30, 90];
const BEATS_PER_BAR = 4;
const BEATS_PER_PHRASE = 16;
const SYNTH_BPM = 100;
const SAFE_FLOOR = 0.55;         // the medallion behind the cover keeps 55%
const PIECES_W = 64, PIECES_H = 32;

const TIER = {
  0: { size: 1400, projection: false, projLeads: false, shafts: 0, dust: 0, clouds: true, columns: false },
  1: { size: 1400, projection: true, projLeads: false, shafts: 6, dust: 0, clouds: true, columns: true },
  2: { size: 2048, projection: true, projLeads: true, shafts: 9, dust: 300, clouds: true, columns: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function lerp(a, b, t) { return a + (b - a) * t; }

// The build's check picture: a random colour per id from this exact LCG. The
// `?rosaceid=1` view reproduces it from the same sequence, so the two can be
// compared pixel for pixel.
function checkPalette(count) {
  let seed = 12345;
  const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  const out = [];
  for (let id = 0; id <= count; id++) out.push([Math.floor(60 + rnd() * 195), Math.floor(60 + rnd() * 195), Math.floor(60 + rnd() * 195)]);
  return out;
}

// =============================================================================
// SHADERS
// =============================================================================

// Bounded hash: every input is a fraction in [0, 1) and the products stay
// under 30, so it survives the TV's fp16 mediump. The rose and the floor
// declare highp anyway - the id decode needs it - but the sky noise runs on
// the same function.
const HASH_GLSL = `
  float hash21(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 1.9);
    return fract(p.x * p.y * 7.31);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    vec2 s = i / 64.0;            // bounded: the noise is asked over |p| < 64
    float a = hash21(s), b = hash21(s + vec2(1.0 / 64.0, 0.0));
    float c = hash21(s + vec2(0.0, 1.0 / 64.0)), d = hash21(s + vec2(1.0 / 64.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
`;

// The quad's four vertices cannot carry the safe-zone attenuation (it is a 2D
// function, not a plane), so the clip position travels to the fragment and
// the shared function runs there, remapped to this piece's 0.55 floor.
const ROSE_VERTEX = `
  varying vec2 vUv;
  varying vec4 vClip;
  void main() {
    vUv = uv;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vClip = clip;
    gl_Position = clip;
  }
`;

const ROSE_FRAGMENT = `
  precision highp float;
  uniform sampler2D uGlass;
  uniform sampler2D uIds;
  uniform sampler2D uMasks;
  uniform sampler2D uPieces;
  uniform vec2 uCentre;       // rose centre, uv
  uniform float uRadius;      // rose radius, uv (of the width)
  uniform vec2 uSun;          // unit direction to the sun in the rose plane
  uniform vec3 uSunColor;     // linear
  uniform float uCloud;       // 1 clear .. 0.5 under a cloud
  uniform float uGain;
  uniform float uTexel;       // one texel of the masks, in uv
  uniform float uTime;
  uniform float uIdDebug;
  varying vec2 vUv;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}
${HASH_GLSL}
  void main() {
    vec4 m = texture2D(uMasks, vUv);
    if (m.b > 0.5) discard;                        // beyond the disc: the wall
    vec4 idc = texture2D(uIds, vUv);
    float id = floor(idc.r * 255.0 + 0.5) * 256.0 + floor(idc.g * 255.0 + 0.5);
    vec2 pc = vec2(mod(id, ${PIECES_W}.0), floor(id / ${PIECES_W}.0));
    vec4 pd = texture2D(uPieces, (pc + 0.5) / vec2(${PIECES_W}.0, ${PIECES_H}.0));
    if (uIdDebug > 0.5) {
      // the build's check picture: piece colour, black leads, black outside
      gl_FragColor = vec4(id > 0.5 ? pd.gba : vec3(0.0), 1.0);
      return;
    }
    vec3 g = texture2D(uGlass, vUv).rgb;
    g = g * g;                                      // the photo, roughly decoded
    // transmission noise: the glass is never perfectly even (4%)
    float grain = 1.0 + 0.04 * (hash21(floor(vUv * 700.0) / 700.0) - 0.5) * 2.0;
    // the sky through the light pieces: pale glass shows the clouds behind it
    float see = smoothstep(0.30, 0.75, max(g.r, max(g.g, g.b)));
    float t = mod(uTime * 0.02, 32.0);
#ifdef SKY_DETAIL
    float cl = vnoise(vUv * 6.0 + vec2(t, 0.0)) * 0.6 + vnoise(vUv * 13.0 + vec2(t * 1.7, 3.0)) * 0.4;
#else
    float cl = vnoise(vUv * 6.0 + vec2(t, 0.0));
#endif
    vec3 sky = mix(vec3(0.62, 0.76, 1.0), vec3(1.0), smoothstep(0.40, 0.70, cl));
    vec3 glass = mix(g, g * sky, see * 0.30) * grain;
    vec3 light = uSunColor * uCloud * pd.r;
    vec3 col = glass * light * uGain;

    // the stone tracery: unlit stone, only the sun's side catches a little
    vec2 rel = (vUv - uCentre) / max(uRadius, 1e-4);
    float facing = 0.5 + 0.5 * dot(normalize(rel + vec2(1e-5)), uSun);
    vec3 stone = g * (0.10 + 0.14 * facing * uCloud) * uSunColor;
    col = mix(col, stone, m.g);
    // the leads: black, with a metallic glint on the edge that faces the sun
    float open = texture2D(uMasks, vUv + uSun * uTexel * 2.5).r;
    float glint = m.r * (1.0 - open) * 0.5;
    vec3 lead = vec3(0.012) + uSunColor * glint * 0.28 * uCloud;
    col = mix(col, lead, m.r);

    float att = safeZoneAttenuation(vClip);
    att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((att - 0.32) / 0.68, 0.0, 1.0));
    gl_FragColor = vec4(col * att, 1.0);
  }
`;

const WORLD_VERTEX = `
  varying vec3 vWorld;
  varying vec4 vClip;
  void main() {
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    vec4 clip = projectionMatrix * viewMatrix * w;
    vClip = clip;
    gl_Position = clip;
  }
`;

// The wall: dark dressed stone in courses, with joints. It only exists to be
// almost black around the rose - the corners of the photo were black too.
const WALL_FRAGMENT = `
  precision mediump float;
  uniform vec3 uRoseCentre;
  uniform float uRoseR;
  uniform vec3 uSunColor;
  uniform float uCloud;
  varying vec3 vWorld;
  varying vec4 vClip;
${HASH_GLSL}
  void main() {
    float row = floor(vWorld.y / 0.62);
    float x = vWorld.x + mod(row, 2.0) * 0.65;
    float col = floor(x / 1.3);
    vec2 f = vec2(fract(x / 1.3), fract(vWorld.y / 0.62));
    float joint = min(1.0, min(f.x * 28.0, f.y * 14.0));
    float tone = 0.011 + 0.005 * hash21(fract(vec2(col, row) / 97.0));
    float d = distance(vWorld.xy, uRoseCentre.xy);
    float e = max(0.0, 1.0 - max(0.0, d - uRoseR) / 4.0);
    float spill = 0.035 * e * e * uCloud;
    vec3 c = vec3(tone) * (0.45 + 0.55 * joint) + spill * uSunColor;
    gl_FragColor = vec4(c, 1.0);
  }
`;

// The floor: stone slabs, and the rose thrown on them. Every floor point looks
// back along the light direction to the rose plane: P = F - L * (F.z / L.z).
// Where P falls inside the disc, the floor takes the glass's colour there at
// that piece's intensity - so a wave that crosses the rose crosses its
// reflection too - blurred by the mip bias, at 60%.
const FLOOR_FRAGMENT = `
  precision highp float;
  uniform sampler2D uGlass;
  uniform sampler2D uIds;
  uniform sampler2D uMasks;
  uniform sampler2D uPieces;
  uniform vec3 uRoseCentre;   // world
  uniform float uRoseR;       // world
  uniform vec2 uCentre;       // uv
  uniform float uRadius;      // uv
  uniform vec3 uLight;        // light travel direction (into the room)
  uniform vec3 uSunColor;
  uniform float uCloud;
  uniform float uGain;
  uniform float uOpacity;
  uniform float uLeads;
  uniform float uBlur;
  uniform float uProjection;
  varying vec3 vWorld;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}
${HASH_GLSL}
  void main() {
    // slabs 1.4 x 0.9, staggered
    float row = floor(vWorld.z / 0.9);
    float x = vWorld.x + mod(row, 2.0) * 0.7;
    float col = floor(x / 1.4);
    vec2 f = vec2(fract(x / 1.4), fract(vWorld.z / 0.9));
    float joint = min(1.0, min(f.x * 33.0, f.y * 20.0));
    float tone = 0.012 + 0.005 * hash21(fract(vec2(col, row) / 89.0));
    vec3 c = vec3(tone) * (0.5 + 0.5 * joint);
    // distance haze: the far floor goes darker still
    c *= 1.0 / (1.0 + vWorld.z * 0.04);

    if (uProjection > 0.5) {
      float t = vWorld.z / uLight.z;
      vec3 P = vWorld - uLight * t;
      vec2 uv = uCentre + (P.xy - uRoseCentre.xy) / (2.0 * uRoseR) * (2.0 * uRadius);
      if (length((uv - uCentre) / uRadius) < 1.0) {
        vec4 m = texture2D(uMasks, uv, uBlur);
        vec4 idc = texture2D(uIds, uv);
        float id = floor(idc.r * 255.0 + 0.5) * 256.0 + floor(idc.g * 255.0 + 0.5);
        vec2 pc = vec2(mod(id, ${PIECES_W}.0), floor(id / ${PIECES_W}.0));
        float lit = texture2D(uPieces, (pc + 0.5) / vec2(${PIECES_W}.0, ${PIECES_H}.0)).r;
        vec3 g = texture2D(uGlass, uv, uBlur).rgb;
        g = g * g;
        float open = (1.0 - m.b) * (1.0 - m.g);            // no light through stone or outside
        open *= 1.0 - uLeads * m.r * 0.7;                  // softened lead network
        c += g * lit * uSunColor * uCloud * uGain * uOpacity * open * (0.6 + 0.4 * joint);
      }
    }
    float att = safeZoneAttenuation(vClip);
    att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((att - 0.32) / 0.68, 0.0, 1.0));
    gl_FragColor = vec4(c * att, 1.0);
  }
`;

const COLUMN_FRAGMENT = `
  precision mediump float;
  varying vec3 vWorld;
  varying vec4 vClip;
  uniform float uHalfW;
  uniform float uX;
${HASH_GLSL}
  void main() {
    float u = (vWorld.x - uX) / uHalfW;             // -1 .. 1 across the shaft
    float shade = 0.006 + 0.008 * (1.0 - u * u);    // a round column, unlit
    float drum = smoothstep(0.0, 0.06, fract(vWorld.y / 1.7));
    gl_FragColor = vec4(vec3(shade * (0.6 + 0.4 * drum)), 1.0);
  }
`;

// Shafts: soft quads along the light direction, one colour each (the glass
// they start from), fading with distance from the rose and from their axis.
const SHAFT_VERTEX = `
  attribute float aAlong;
  attribute float aSide;
  attribute vec3 aColor;
  attribute float aAlpha;
  varying float vAlong;
  varying float vSide;
  varying vec3 vColor;
  varying float vAlpha;
  varying vec4 vClip;
  void main() {
    vAlong = aAlong; vSide = aSide; vColor = aColor; vAlpha = aAlpha;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vClip = clip;
    gl_Position = clip;
  }
`;
const SHAFT_FRAGMENT = `
  precision mediump float;
  uniform vec3 uSunColor;
  uniform float uCloud;
  uniform vec3 uRoseNdc;      // rose centre (x * aspect, y) and radius, in height units
  varying float vAlong;
  varying float vSide;
  varying vec3 vColor;
  varying float vAlpha;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    float edge = 1.0 - vSide * vSide;
    edge *= edge;
    float len = smoothstep(0.0, 0.08, vAlong) * (1.0 - vAlong * 0.85);
    float att = safeZoneAttenuation(vClip);
    att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((att - 0.32) / 0.68, 0.0, 1.0));
    // not over the window itself: the beam appears from the rim outward
    vec2 ndc = vClip.xy / max(vClip.w, 0.0001);
    float dRose = length(vec2(ndc.x * uAspect, ndc.y) - uRoseNdc.xy);
    float clear = smoothstep(uRoseNdc.z * 0.92, uRoseNdc.z * 1.12, dRose);
    gl_FragColor = vec4(vColor * uSunColor * (edge * len * vAlpha * uCloud * att * clear), 1.0);
  }
`;

const DUST_VERTEX = `
  attribute float aPhase;
  varying float vA;
  uniform float uTime;
  uniform float uDrift;
  void main() {
    vec3 p = position;
    p.y += sin(uTime * 0.31 + aPhase * 6.2831) * 0.12;
    p.x += sin(uTime * 0.23 + aPhase * 9.1) * 0.10;
    p.z += mod(uTime * 0.06 + aPhase * 6.0, 6.0) - 3.0;
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = 1.7 * (10.0 / max(-mv.z, 1.0));
    vA = 0.55 + 0.45 * sin(uTime * 0.9 + aPhase * 40.0);
  }
`;
const DUST_FRAGMENT = `
  precision mediump float;
  uniform vec3 uSunColor;
  uniform float uCloud;
  varying float vA;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float a = smoothstep(0.5, 0.15, length(d));
    gl_FragColor = vec4(uSunColor * (a * vA * 0.045 * uCloud), 1.0);
  }
`;

// =============================================================================
// THE VISUALIZER
// =============================================================================

export class RosaceVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._beatClock = 0;
    this._hb = 0;
    this._lastBeat = null;
    this._lastBarIndex = null;
    this._lastPhraseIndex = null;
    this._synthBeat = 0;
    this._radials = [];          // half-beat of launch, newest last
    this._angularHb = -1e9;      // half-beat the angular front left the top
    this._angularBarLen = 8;     // half-beats in the bar it sweeps
    this._petalUntilHb = -1e9;
    this._petalSector = 0;
    this._petalCount = 0;
    this._dropAt = -1e9;
    this._lastDropTime = -1e9;
    this._lowBand = 0; this._midBand = 0; this._highBand = 0; this._live = false;
    this._progress = 0;
    this._lastPalette = null;
    this._tint = null;           // linear rgb, max channel 1
    this._cloud = 1;             // the sun's strength under the clouds
    this._cloudPhase = 'clear';  // clear | in | hold | out
    this._cloudT = 0;
    this._cloudNext = lerp(CLOUD_EVERY[0], CLOUD_EVERY[1], Math.random());
    this._cloudHold = 0;
    this._cloudEvents = 0;
    this._aspect = 0;

    this._sunOverride = urlParam('rosacesun');
    this._sunOverride = this._sunOverride != null && this._sunOverride !== '' ? clamp01(parseFloat(this._sunOverride)) : null;
    this._forceCloud = urlParam('rosacecloud') === '1';
    this._idDebug = urlParam('rosaceid') === '1';
    const pane = urlParam('rosacepane');
    this._pane = pane != null && pane !== '' ? parseInt(pane, 10) : null;
    if (this._forceCloud) this._cloudNext = 1.0;

    this._postProcessing = this._idDebug
      ? { bloomStrength: 0, vignette: 0, grain: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }
      : { bloomStrength: 0.55, vignette: 0.35, aberration: 0.0012 };
    if (this._idDebug) this._layout = TIER[0];
  }

  get postProcessing() { return this._postProcessing; }

  // Where the camera's ray through an NDC point meets the wall plane z = 0.
  _onWall(ndcX, ndcY) {
    const cam = this._camera;
    const p = new THREE.Vector3(ndcX, ndcY, 0.5).unproject(cam);
    const o = cam.position;
    const dir = p.sub(o);
    const t = -o.z / dir.z;
    return new THREE.Vector3(o.x + dir.x * t, o.y + dir.y * t, 0);
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const L = this._layout;
    const assets = getRosace();
    this._assets = assets;
    // A forced or early start can run before the preload has landed (the Pi
    // took 14 s to show pieces: 0 for exactly this reason). Build the room
    // now, and rebuild the whole piece once the assets arrive.
    if (!assets) {
      const self = this;
      preloadRosace().then(function (a) {
        if (!a || !self._scene || self._assets) return;
        const s = self._scene, c = self._camera;
        self.dispose();
        self.init(s, c);
      });
    }

    camera.position.set(0, 0, CAMERA_D);
    // the id view looks straight at the wall: no keystone, so the picture
    // compares pixel for pixel with the build's
    camera.lookAt(0, this._idDebug ? 0 : CAMERA_D * Math.tan(PITCH_DEG * Math.PI / 180), 0);
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();

    // --- the rose, placed on the wall by unprojection
    const top = this._onWall(0, ROSE_Y_NDC + ROSE_SHARE);
    const bot = this._onWall(0, ROSE_Y_NDC - ROSE_SHARE);
    const R = (top.y - bot.y) / 2;
    const cy = (top.y + bot.y) / 2;
    this._R = R;
    this._roseCentre = new THREE.Vector3(0, cy, 0);
    this._floorY = this._onWall(0, FLOOR_NDC).y;

    const pieces = assets ? assets.data : { count: 0, centre: [0.5, 0.5], radius: 0.5, pieces: [] };
    this._pieces = pieces.pieces;
    this._count = pieces.count;
    // The build's centre is in image coordinates (y down); the texture's v is
    // up, so the uv centre flips y. Same flip for every piece below.
    this._uvCentre = new THREE.Vector2(pieces.centre[0], 1 - pieces.centre[1]);
    this._uvRadius = pieces.radius;
    const n = this._count;
    this._val = new Float32Array(n + 1).fill(GRADIENT_MIN);
    this._target = new Float32Array(n + 1);
    this._ring = new Uint8Array(n + 1);
    this._sector = new Uint8Array(n + 1);
    this._px = new Float32Array(n + 1);     // unit position from the centre, y up
    this._py = new Float32Array(n + 1);
    this._rad = new Float32Array(n + 1);
    for (const q of this._pieces) {
      const i = q.id;
      this._rad[i] = q.radius;
      this._px[i] = Math.cos(q.angle); this._py[i] = Math.sin(q.angle);
      this._ring[i] = Math.min(RINGS - 1, Math.floor(q.radius * RINGS));
      // sectors count clockwise from the top
      const fromTop = (Math.PI / 2 - q.angle + Math.PI * 4) % (Math.PI * 2);
      this._sector[i] = Math.min(SECTORS - 1, Math.floor(fromTop / (Math.PI * 2 / SECTORS)));
    }

    // per-piece data texture: R = intensity, GBA = the check colour
    this._piecesData = new Float32Array(PIECES_W * PIECES_H * 4);
    const pal = checkPalette(n);
    for (let i = 0; i <= n && i < PIECES_W * PIECES_H; i++) {
      this._piecesData[i * 4] = i === 0 ? 0 : GRADIENT_MIN;
      this._piecesData[i * 4 + 1] = pal[i][0] / 255;
      this._piecesData[i * 4 + 2] = pal[i][1] / 255;
      this._piecesData[i * 4 + 3] = pal[i][2] / 255;
    }
    this._piecesTex = new THREE.DataTexture(this._piecesData, PIECES_W, PIECES_H, THREE.RGBAFormat, THREE.FloatType);
    this._piecesTex.minFilter = THREE.NearestFilter;
    this._piecesTex.magFilter = THREE.NearestFilter;
    this._piecesTex.generateMipmaps = false;
    this._piecesTex.needsUpdate = true;

    const glass = assets ? assets.glass : null;
    const ids = assets ? assets.ids : null;
    const masks = assets ? assets.masks : null;
    const texel = masks && masks.image ? 1 / masks.image.width : 1 / 2048;

    this._shared = {
      uSun: { value: new THREE.Vector2(-1, 0) },
      uSunColor: { value: new THREE.Vector3(1, 1, 1) },
      uCloud: { value: 1 },
      uGain: { value: GLASS_GAIN },
      uTime: { value: 0 }
    };
    this._roseUniforms = Object.assign({
      uGlass: { value: glass }, uIds: { value: ids }, uMasks: { value: masks }, uPieces: { value: this._piecesTex },
      uCentre: { value: this._uvCentre }, uRadius: { value: this._uvRadius },
      uTexel: { value: texel }, uIdDebug: { value: this._idDebug ? 1 : 0 }
    }, this._shared, safeZoneUniforms);
    this._roseMat = new THREE.ShaderMaterial({
      uniforms: this._roseUniforms, vertexShader: ROSE_VERTEX, fragmentShader: ROSE_FRAGMENT,
      defines: this._tier >= 2 ? { SKY_DETAIL: 1 } : {},
      depthWrite: true, depthTest: true, transparent: false
    });
    // The quad covers the disc's bounding square; the shader discards outside.
    const s = 2 * R / (2 * this._uvRadius);     // uv 0..1 spans s world units
    this._roseGeo = new THREE.PlaneGeometry(s, s);
    this._rose = new THREE.Mesh(this._roseGeo, this._roseMat);
    this._rose.position.set(
      this._roseCentre.x - (this._uvCentre.x - 0.5) * s,
      this._roseCentre.y - (this._uvCentre.y - 0.5) * s, 0.0);
    this._rose.frustumCulled = false;
    this._rose.renderOrder = 0;
    scene.add(this._rose);
    if (!assets) this._rose.visible = false;

    // --- the wall
    this._wallUniforms = Object.assign({
      uRoseCentre: { value: this._roseCentre }, uRoseR: { value: R }
    }, this._shared);
    this._wallMat = new THREE.ShaderMaterial({ uniforms: this._wallUniforms, vertexShader: WORLD_VERTEX, fragmentShader: WALL_FRAGMENT });
    this._wallGeo = new THREE.PlaneGeometry(WALL_W, WALL_W);
    this._wall = new THREE.Mesh(this._wallGeo, this._wallMat);
    this._wall.position.set(0, this._floorY + WALL_W / 2, -0.03);
    this._wall.renderOrder = 2;
    // the id view is the rose alone on black, to be compared with the build's picture
    if (!this._idDebug) scene.add(this._wall);

    // --- the floor
    this._floorUniforms = Object.assign({
      uGlass: { value: glass }, uIds: { value: ids }, uMasks: { value: masks }, uPieces: { value: this._piecesTex },
      uRoseCentre: { value: this._roseCentre }, uRoseR: { value: R },
      uCentre: { value: this._uvCentre }, uRadius: { value: this._uvRadius },
      uLight: { value: new THREE.Vector3(0, -1, 1) },
      uOpacity: { value: PROJ_OPACITY }, uLeads: { value: L.projLeads ? 1 : 0 },
      uBlur: { value: PROJ_BLUR_LOD }, uProjection: { value: L.projection && assets ? 1 : 0 }
    }, this._shared, safeZoneUniforms);
    this._floorMat = new THREE.ShaderMaterial({ uniforms: this._floorUniforms, vertexShader: WORLD_VERTEX, fragmentShader: FLOOR_FRAGMENT });
    this._floorGeo = new THREE.PlaneGeometry(WALL_W, FLOOR_DEPTH);
    this._floor = new THREE.Mesh(this._floorGeo, this._floorMat);
    this._floor.rotation.x = -Math.PI / 2;
    this._floor.position.set(0, this._floorY, FLOOR_DEPTH / 2);
    this._floor.renderOrder = 1;
    if (!this._idDebug) scene.add(this._floor);

    // --- columns (silhouettes at the frame's sides)
    this._columns = [];
    if (L.columns) {
      for (const sgn of [-1, 1]) {
        const cx = this._onWall(sgn * COLUMN_X_NDC, 0).x;
        const hw = (this._onWall(COLUMN_W_NDC, 0).x - this._onWall(0, 0).x) / 2;
        const mat = new THREE.ShaderMaterial({
          uniforms: { uHalfW: { value: hw }, uX: { value: cx } }, vertexShader: WORLD_VERTEX, fragmentShader: COLUMN_FRAGMENT
        });
        const geo = new THREE.PlaneGeometry(hw * 2, WALL_W);
        const m = new THREE.Mesh(geo, mat);
        m.position.set(cx, this._floorY + WALL_W / 2, 0.4);
        scene.add(m);
        this._columns.push(m);
      }
    }

    // --- shafts
    this._shaftCount = assets ? L.shafts : 0;
    if (this._shaftCount > 0) this._buildShafts();

    // --- dust
    if (L.dust > 0 && assets) this._buildDust(L.dust);

    // tier 2 asks for the 2048 and swaps it in when it lands
    if (assets && L.size > assets.size) {
      const self = this;
      ensureRosaceGlass(L.size).then(function (big) {
        if (!big || !self._roseMat) return;
        self._roseUniforms.uGlass.value = big.glass; self._roseUniforms.uIds.value = big.ids;
        self._floorUniforms.uGlass.value = big.glass; self._floorUniforms.uIds.value = big.ids;
      });
    }

    this._aspect = safeZoneUniforms.uAspect.value || 0;
    this._placeSun(this._sunOverride != null ? this._sunOverride : 0, null);
    this._writePieces(0);
  }

  _buildShafts() {
    const N = this._shaftCount;
    const pos = new Float32Array(N * 4 * 3);
    const along = new Float32Array(N * 4);
    const side = new Float32Array(N * 4);
    const color = new Float32Array(N * 4 * 3);
    const alpha = new Float32Array(N * 4);
    const index = [];
    this._shaftOrigin = [];
    // origins: bright-ish pieces spread around the rose, deterministic
    const candidates = this._pieces.slice().sort((a, b) => (b.color[0] + b.color[1] + b.color[2]) - (a.color[0] + a.color[1] + a.color[2]));
    const pool = candidates.slice(0, Math.max(N * 4, 24));
    for (let k = 0; k < N; k++) {
      const q = pool[(k * 7) % pool.length];
      this._shaftOrigin.push(q);
      const c = q.color;
      const mx = Math.max(1, c[0], c[1], c[2]);
      for (let v = 0; v < 4; v++) {
        along[k * 4 + v] = v < 2 ? 0 : 1;
        side[k * 4 + v] = (v % 2 === 0) ? -1 : 1;
        color[(k * 4 + v) * 3] = Math.pow(c[0] / mx, 2.0);
        color[(k * 4 + v) * 3 + 1] = Math.pow(c[1] / mx, 2.0);
        color[(k * 4 + v) * 3 + 2] = Math.pow(c[2] / mx, 2.0);
        alpha[k * 4 + v] = 0.10 + 0.05 * ((k * 5) % 3) / 2;
      }
      const b = k * 4;
      index.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
    }
    const geo = new THREE.BufferGeometry();
    this._shaftPos = new THREE.BufferAttribute(pos, 3);
    this._shaftPos.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', this._shaftPos);
    geo.setAttribute('aAlong', new THREE.BufferAttribute(along, 1));
    geo.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
    geo.setAttribute('aColor', new THREE.BufferAttribute(color, 3));
    this._shaftAlpha = new THREE.BufferAttribute(alpha, 1);
    this._shaftAlpha.setUsage(THREE.DynamicDrawUsage);
    this._shaftBase = Float32Array.from(alpha);
    geo.setAttribute('aAlpha', this._shaftAlpha);
    geo.setIndex(index);
    this._shaftGeo = geo;
    this._roseNdc = new THREE.Vector3(0, 0.25, 0.7);
    this._shaftMat = new THREE.ShaderMaterial({
      uniforms: Object.assign({ uRoseNdc: { value: this._roseNdc } }, this._shared, safeZoneUniforms),
      vertexShader: SHAFT_VERTEX, fragmentShader: SHAFT_FRAGMENT,
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: true, side: THREE.DoubleSide
    });
    this._shafts = new THREE.Mesh(geo, this._shaftMat);
    this._shafts.frustumCulled = false;
    this._shafts.renderOrder = 10;
    this._scene.add(this._shafts);
  }

  _buildDust(count) {
    const pos = new Float32Array(count * 3);
    const phase = new Float32Array(count);
    const R = this._R;
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() * 2 - 1) * R * 1.1;
      pos[i * 3 + 1] = this._floorY + Math.random() * (this._roseCentre.y + R - this._floorY);
      pos[i * 3 + 2] = 0.5 + Math.random() * 6;
      phase[i] = Math.random();
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1));
    this._dustGeo = geo;
    this._dustMat = new THREE.ShaderMaterial({
      uniforms: Object.assign({ uDrift: { value: 1 } }, this._shared),
      vertexShader: DUST_VERTEX, fragmentShader: DUST_FRAGMENT,
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: true
    });
    this._dust = new THREE.Points(geo, this._dustMat);
    this._dust.frustumCulled = false;
    this._dust.renderOrder = 11;
    this._scene.add(this._dust);
  }

  // --- the sun ---------------------------------------------------------------

  // progress 0: low on the left; 0.5: zenith; 1: low on the right. The colour
  // goes orange to white with the elevation, tinted a quarter by the cover.
  _placeSun(progress, palette) {
    const theta = (200 - 220 * progress) * Math.PI / 180;
    const sx = Math.cos(theta), sy = Math.sin(theta);
    this._sunX = sx; this._sunY = sy;
    this._shared.uSun.value.set(sx, sy);
    const el = clamp01(sy);
    const w = Math.pow(Math.min(1, el / 0.6), 0.8);
    let r = lerp(SUN_ORANGE[0], 1, w), g = lerp(SUN_ORANGE[1], 1, w), b = lerp(SUN_ORANGE[2], 1, w);
    if (this._tint) {
      r = lerp(r, r * this._tint[0], PALETTE_TINT);
      g = lerp(g, g * this._tint[1], PALETTE_TINT);
      b = lerp(b, b * this._tint[2], PALETTE_TINT);
    }
    // the sun under the horizon lights nothing much
    const strength = 0.80 + 0.20 * clamp01((sy + 0.15) / 0.3);
    this._shared.uSunColor.value.set(r * strength, g * strength, b * strength);

    // the beam into the room: sideways with the sun's azimuth, always steep
    // enough to keep the ellipse on the visible floor
    const light = this._floorUniforms.uLight.value;
    light.set(-sx * 0.32, -(1.0 + 0.8 * el), 1.0);
    this._light = light;
    if (this._shafts) this._layShafts(light);
  }

  // The rose disc on screen, for the shafts' fade: centre and radius in the
  // safe zone's height units (x scaled by the aspect).
  _placeRoseNdc() {
    const cam = this._camera;
    const aspect = safeZoneUniforms.uAspect.value || 1;
    _v1.copy(this._roseCentre).project(cam);
    _v2.set(this._roseCentre.x + this._R, this._roseCentre.y, 0).project(cam);
    this._roseNdc.set(_v1.x * aspect, _v1.y, Math.abs(_v2.x - _v1.x) * aspect);
  }

  _layShafts(light) {
    const N = this._shaftCount;
    const a = this._shaftPos.array;
    const cam = this._camera.position;
    const dir = _v1.copy(light).normalize();
    const R = this._R;
    for (let k = 0; k < N; k++) {
      const q = this._shaftOrigin[k];
      // the piece's centroid in world (image y down -> world y up)
      const ox = this._rose.position.x + (q.cx - 0.5) * (2 * R / (2 * this._uvRadius));
      const oy = this._rose.position.y + (0.5 - q.cy) * (2 * R / (2 * this._uvRadius));
      const len = Math.min(14, (oy - this._floorY) / Math.max(0.2, -dir.y));
      const w = 0.22 + 0.18 * ((k * 3) % 4) / 3;
      // billboard: side vector perpendicular to the beam and to the view
      _v2.set(ox, oy, 0).sub(cam);
      _v3.crossVectors(dir, _v2).normalize().multiplyScalar(w);
      const ex = ox + dir.x * len, ey = oy + dir.y * len, ez = dir.z * len;
      const o = k * 12;
      a[o] = ox - _v3.x; a[o + 1] = oy - _v3.y; a[o + 2] = 0.05 - _v3.z;
      a[o + 3] = ox + _v3.x; a[o + 4] = oy + _v3.y; a[o + 5] = 0.05 + _v3.z;
      a[o + 6] = ex - _v3.x * 1.6; a[o + 7] = ey - _v3.y * 1.6; a[o + 8] = ez - _v3.z * 1.6;
      a[o + 9] = ex + _v3.x * 1.6; a[o + 10] = ey + _v3.y * 1.6; a[o + 11] = ez + _v3.z * 1.6;
    }
    this._shaftPos.needsUpdate = true;
  }

  _setTint(profile) {
    const palette = profile.artworkPalette;
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : null;
    if (!vivid) { this._tint = null; return; }
    const hex = vivid[0];
    const r = ((hex >> 16) & 255) / 255, g = ((hex >> 8) & 255) / 255, b = (hex & 255) / 255;
    const mx = Math.max(r, g, b, 1e-3);
    this._tint = [r / mx, g / mx, b / mx];
  }

  // --- the clock (Hex's) -----------------------------------------------------

  _advanceClock(dt, profile, bpm) {
    const hasGrid = profile.beatIndex != null && profile.beatPhase != null;
    let beatClock;
    if (hasGrid) {
      beatClock = profile.beatIndex + profile.beatPhase;
      if (this._lastBeat != null && Math.abs(beatClock - this._beatClock) > 4) this._resync();
    } else {
      this._synthBeat += dt * bpm / 60;
      beatClock = this._synthBeat;
    }
    this._beatClock = beatClock;
    this._hb = beatClock * 2;
    const beat = Math.floor(beatClock);
    if (this._lastBeat == null) { this._lastBeat = beat; return; }
    while (this._lastBeat < beat) {
      this._lastBeat++;
      this._onBeat(this._lastBeat * 2);
      if (hasGrid) {
        const bar = profile.barIndex;
        if (bar != null && bar !== this._lastBarIndex) {
          if (this._lastBarIndex != null) this._onBar(this._lastBeat * 2);
          this._lastBarIndex = bar;
        }
        const phrase = profile.phraseIndex;
        if (phrase != null && phrase !== this._lastPhraseIndex) {
          if (this._lastPhraseIndex != null) this._onPhrase(this._lastBeat * 2);
          this._lastPhraseIndex = phrase;
        }
      } else {
        if (this._lastBeat % BEATS_PER_BAR === 0) this._onBar(this._lastBeat * 2);
        if (this._lastBeat % BEATS_PER_PHRASE === 0) this._onPhrase(this._lastBeat * 2);
      }
    }
  }

  _resync() {
    this._radials.length = 0;
    this._angularHb = -1e9;
    this._petalUntilHb = -1e9;
    this._dropAt = -1e9;
    this._lastBeat = null;
    this._lastBarIndex = null;
    this._lastPhraseIndex = null;
  }

  _onBeat(hb) {
    this._radials.push(hb);
    if (this._radials.length > 4) this._radials.shift();
  }
  _onBar(hb) {
    this._angularHb = hb;
    this._angularBarLen = BEATS_PER_BAR * 2;
  }
  _onPhrase(hb) {
    this._petalSector = (this._petalCount * 5) % SECTORS;
    this._petalCount++;
    this._petalUntilHb = hb + BEATS_PER_BAR * 2;
  }

  // --- the frame -------------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    this._time += dt;
    this._shared.uTime.value = this._time;

    const palette = profile.artworkPalette;
    if (palette !== this._lastPalette) { this._lastPalette = palette; this._setTint(profile); }

    const director = profile.director || {};
    this._live = !!(director.hasReliableGrid && director.bands);
    if (this._live) {
      this._lowBand = clamp01(director.bands.low);
      this._midBand = clamp01(director.bands.mid);
      this._highBand = clamp01(director.bands.high);
    }

    this._progress = this._sunOverride != null ? this._sunOverride : clamp01(profile.progress || 0);
    this._placeSun(this._progress, palette);

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._advanceClock(dt, profile, bpm);

    const ev = director.nextEvent;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0 && this._time - this._lastDropTime > 2.0) {
      this._lastDropTime = this._time;
      this._dropAt = this._hb;
    }

    this._advanceCloud(dt);
    this._writePieces(dt);
    if (this._shafts) this._placeRoseNdc();

    // a resize moves the columns and the floor edge with it
    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && aspect !== this._aspect) { this._aspect = aspect; this._reframe(); }
  }

  _advanceCloud(dt) {
    if (!this._layout.clouds) return;
    this._cloudT += dt;
    switch (this._cloudPhase) {
      case 'clear':
        if (this._cloudT >= this._cloudNext) { this._cloudPhase = 'in'; this._cloudT = 0; this._cloudEvents++; }
        this._cloud = 1;
        break;
      case 'in':
        this._cloud = lerp(1, CLOUD_MIN, clamp01(this._cloudT / CLOUD_IN_S));
        if (this._cloudT >= CLOUD_IN_S) { this._cloudPhase = 'hold'; this._cloudT = 0; this._cloudHold = lerp(CLOUD_HOLD[0], CLOUD_HOLD[1], Math.random()); }
        break;
      case 'hold':
        this._cloud = CLOUD_MIN;
        if (this._cloudT >= this._cloudHold) { this._cloudPhase = 'out'; this._cloudT = 0; }
        break;
      case 'out':
        this._cloud = lerp(CLOUD_MIN, 1, clamp01(this._cloudT / CLOUD_OUT_S));
        if (this._cloudT >= CLOUD_OUT_S) {
          this._cloudPhase = 'clear'; this._cloudT = 0;
          this._cloudNext = this._forceCloud ? 6 : lerp(CLOUD_EVERY[0], CLOUD_EVERY[1], Math.random());
        }
        break;
    }
    this._shared.uCloud.value = this._cloud;
  }

  // Every piece's target this frame, then the attack/decay toward it.
  _writePieces(dt) {
    const n = this._count;
    const hb = this._hb;
    const T = this._target;
    const sx = this._sunX, sy = this._sunY;
    const up = dt > 0 ? Math.min(1, dt / ATTACK_S) : 1;
    const down = dt > 0 ? Math.min(1, dt / DECAY_S) : 1;
    const petalOn = hb < this._petalUntilHb;
    const s0 = this._petalSector;
    // the angular front: sector index at this half-beat, with its trail
    const angAge = hb - this._angularHb;
    const angSector = angAge >= 0 && angAge < this._angularBarLen ? Math.floor(angAge / this._angularBarLen * SECTORS) : -1;
    // the drop: 1.6 for a beat, then two bars down
    let dropMix = 0;
    if (this._dropAt > -1e8) {
      const age = hb - this._dropAt;
      if (age < 2) dropMix = 1;
      else dropMix = Math.max(0, 1 - (age - 2) / (BEATS_PER_BAR * 2 * 2));
    }

    for (let i = 1; i <= n; i++) {
      const facing = 0.5 + 0.5 * (this._px[i] * sx + this._py[i] * sy);
      let base = lerp(GRADIENT_MIN, GRADIENT_MAX, facing);
      if (this._live) {
        const r = this._rad[i];
        let band;
        if (r < 0.2 || this._py[i] > 0.45) band = this._highBand;
        else if (r > 0.35 && r < 0.7 && Math.abs(this._py[i]) <= 0.45) band = this._midBand;
        else band = this._lowBand;
        base *= 1 + BAND_SWING * (band * 2 - 1);
      }
      let add = 0;
      const ring = this._ring[i];
      for (let w = 0; w < this._radials.length; w++) {
        const age = Math.floor(hb - this._radials[w]);
        if (age < 0 || age >= RINGS + 3) continue;
        const d = age - ring;
        if (d === 0) add += RADIAL_AMP;
        else if (d > 0 && d <= 3) add += RADIAL_AMP * Math.pow(TRAIL, d);
      }
      if (angSector >= 0) {
        const d = (angSector - this._sector[i] + SECTORS) % SECTORS;
        if (d === 0) add += ANGULAR_AMP;
        else if (d <= 2) add += ANGULAR_AMP * Math.pow(TRAIL, d);
      }
      let t = base + add;
      if (petalOn && this._sector[i] === s0 && this._rad[i] >= PETAL_R[0] && this._rad[i] <= PETAL_R[1]) t = Math.max(t, PETAL_LEVEL);
      if (dropMix > 0) t = Math.max(t, lerp(base, DROP_LEVEL, dropMix));
      if (this._pane != null) t = i === this._pane ? CAP : 0.10;
      T[i] = Math.min(CAP, t);
    }
    const V = this._val, D = this._piecesData;
    for (let i = 1; i <= n; i++) {
      const t = T[i], v = V[i];
      const nv = v + (t - v) * (t > v ? up : down);
      V[i] = nv;
      D[i * 4] = nv;
    }
    this._piecesTex.needsUpdate = true;
    // the shafts carry their piece's light
    if (this._shafts) {
      const a = this._shaftAlpha.array, b = this._shaftBase;
      for (let k = 0; k < this._shaftCount; k++) {
        const lit = V[this._shaftOrigin[k].id];
        for (let q = 0; q < 4; q++) a[k * 4 + q] = b[k * 4 + q] * lit;
      }
      this._shaftAlpha.needsUpdate = true;
    }
  }

  _reframe() {
    if (!this._columns.length) return;
    for (let k = 0; k < 2; k++) {
      const sgn = k === 0 ? -1 : 1;
      const cx = this._onWall(sgn * COLUMN_X_NDC, 0).x;
      this._columns[k].position.x = cx;
      this._columns[k].material.uniforms.uX.value = cx;
    }
  }

  // --- for the tests -----------------------------------------------------------
  get pieceValues() { return this._val; }
  get cloudLevel() { return this._cloud; }
  get cloudEvents() { return this._cloudEvents; }
  get sun() { return { x: this._sunX, y: this._sunY, color: this._shared.uSunColor.value }; }
  get lightDir() { return this._light; }
  get rings() { return this._ring; }
  get radii() { return this._rad; }
  get radialWaves() { return this._radials; }
  get frame() { return { R: this._R, centre: this._roseCentre, floorY: this._floorY, rose: this._rose }; }

  dispose() {
    const scene = this._scene;
    if (!scene) return;
    for (const m of [this._rose, this._wall, this._floor, this._shafts, this._dust].concat(this._columns || [])) {
      if (!m) continue;
      scene.remove(m);
      if (m.geometry) m.geometry.dispose();
      if (m.material) m.material.dispose();
    }
    if (this._piecesTex) this._piecesTex.dispose();
    this._rose = this._wall = this._floor = this._shafts = this._dust = null;
    this._columns = [];
    this._roseMat = null;
    this._piecesTex = null;
    this._scene = null;
  }
}

const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
